import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/config';
import MfaSetup from './MfaSetup';

const API = BACKEND_URL + '/api';

/**
 * Seconde étape de la connexion (T15). `challenge` est la réponse de `POST /auth/login` quand le mot de passe ne
 * suffit pas : `mfa_required` → code de l'application (ou code de secours) ; `mfa_setup_required` → l'admin met en
 * place sa double authentification, obligatoire, avant tout accès. `onDone(session)` ouvre la session.
 */
export default function MfaLoginStep({ challenge, onDone, onCancel }) {
  const token = challenge.mfa_token;
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const start = useCallback(() => axios.post(`${API}/auth/mfa/setup/start`, { mfa_token: token }).then((r) => r.data), [token]);
  const confirm = useCallback((c) => axios.post(`${API}/auth/mfa/setup/confirm`, { mfa_token: token, code: c }).then((r) => r.data), [token]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/mfa/verify`, { mfa_token: token, code: code.trim() });
      onDone(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Code incorrect.');
      setBusy(false);
    }
  };

  if (challenge.mfa_setup_required) {
    return (
      <div data-testid="mfa-login-setup">
        <div className="form-hd">
          <h1 className="form-title">Double authentification</h1>
          <p className="form-sub">Elle est obligatoire pour votre compte : mettez-la en place pour continuer.</p>
        </div>
        <MfaSetup start={start} confirm={confirm} onDone={onDone} inputClass="fi" buttonClass="cta cta--primary" />
        <button type="button" className="cta cta--ghost" style={{ marginTop: 12 }} onClick={onCancel}>← Retour</button>
      </div>
    );
  }

  return (
    <div data-testid="mfa-login-code">
      <div className="form-hd">
        <h1 className="form-title">Code de vérification</h1>
        <p className="form-sub">
          {backup ? 'Saisissez un de vos codes de secours.' : 'Saisissez le code à 6 chiffres de votre application d’authentification.'}
        </p>
      </div>
      <form onSubmit={verify} noValidate>
        <div className="fg">
          <label className="flbl" htmlFor="mfaCode">{backup ? 'Code de secours' : 'Code'}</label>
          <input id="mfaCode" className={`fi ${error ? 'err' : ''}`} autoComplete="one-time-code"
            inputMode={backup ? 'text' : 'numeric'} maxLength={backup ? 9 : 6}
            placeholder={backup ? 'ABCD-EFGH' : '123456'} value={code}
            onChange={(e) => setCode(backup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
            data-testid="mfa-code" />
          {error && <div className="ferr on" role="alert" data-testid="mfa-error">{error}</div>}
        </div>
        <button type="submit" className="cta cta--primary" disabled={busy || code.length < (backup ? 8 : 6)} data-testid="mfa-submit">
          {busy ? 'Vérification…' : 'Valider'}
        </button>
      </form>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
        <button type="button" className="lnk" style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => { setBackup(!backup); setCode(''); setError(''); }} data-testid="mfa-toggle-backup">
          {backup ? 'Utiliser l’application' : 'Utiliser un code de secours'}
        </button>
        <button type="button" className="lnk" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={onCancel}>
          ← Retour
        </button>
      </div>
    </div>
  );
}
